import { useEffect, useRef, useState, type DragEvent } from "react";
import CloudUpload from "@mui/icons-material/CloudUpload";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { translate, useTranslate } from "../i18n";
import { useLocaleStore } from "../stores/locale-store";
import { GlobalFileInput } from "./GlobalInput";

const MAX_PDF_SIZE = 250 * 1024 * 1024;

type PdfUploadDialogProps = {
  open: boolean;
  progress: number | null;
  onClose: () => void;
  onUpload: (input: { file: File; autoOcrScan: boolean }) => Promise<void>;
  pageLimit?: number;
};

export function fileSize(size: number): string {
  return `${(size / 1024 / 1024).toFixed(size >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
}

export function validatePdf(file: File): string | null {
  const locale = useLocaleStore.getState().locale;
  if (file.type !== "application/pdf" || !file.name.toLowerCase().endsWith(".pdf")) {
    return translate(locale, "กรุณาเลือกไฟล์ PDF เท่านั้น");
  }
  if (file.size > MAX_PDF_SIZE) return translate(locale, "ไฟล์ต้องมีขนาดไม่เกิน 250 MB");
  return null;
}

export function PdfUploadDialog({
  open,
  progress,
  onClose,
  onUpload,
  pageLimit = 3000,
}: PdfUploadDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");
  const [autoOcrScan, setAutoOcrScan] = useState(true);
  const uploading = progress !== null;
  const __ = useTranslate();

  useEffect(() => {
    if (!open) {
      setFile(null);
      setDragging(false);
      setError("");
      setAutoOcrScan(true);
    }
  }, [open]);

  function chooseFile(next: File | undefined) {
    if (!next) return;
    const validationError = validatePdf(next);
    if (validationError) {
      setFile(null);
      setError(validationError);
      return;
    }
    setFile(next);
    setError("");
  }

  function drop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    chooseFile(event.dataTransfer.files[0]);
  }

  async function submit() {
    if (!file || uploading) return;
    setError("");
    try {
      await onUpload({ file, autoOcrScan });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : __("Upload ไม่สำเร็จ"));
    }
  }

  return <Dialog
    open={open}
    onClose={uploading ? undefined : onClose}
    fullWidth
    maxWidth="sm"
  >
    <DialogTitle fontWeight={800}>{__("Upload PDF")}</DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="text.secondary" mb={2}>
          {__("รองรับ PDF สูงสุด 250 MB และ {{count}} หน้า ตามแผนปัจจุบัน", { count: pageLimit })}
      </Typography>
      <FormControlLabel
        sx={{ mb: 2 }}
        control={
          <Switch
            checked={autoOcrScan}
            onChange={(event) => setAutoOcrScan(event.target.checked)}
            disabled={uploading}
          />
        }
        label={__("AUTO OCR SCAN")}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box
        onDragEnter={(event) => {
          event.preventDefault();
          if (!uploading) setDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          if (event.currentTarget === event.target) setDragging(false);
        }}
        onDrop={uploading ? undefined : drop}
        sx={{
          minHeight: 220,
          p: 3,
          border: "2px dashed",
          borderColor: dragging ? "primary.main" : "divider",
          borderRadius: 3,
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          bgcolor: dragging ? "action.hover" : "background.default",
          transition: "border-color .2s ease, background-color .2s ease",
        }}
      >
        {file ? (
          <Stack alignItems="center" spacing={1}>
            <DescriptionOutlined color="primary" sx={{ fontSize: 50 }} />
            <Typography fontWeight={700} sx={{ wordBreak: "break-word" }}>{file.name}</Typography>
            <Typography variant="body2" color="text.secondary">{fileSize(file.size)}</Typography>
            {!uploading && (
              <Button onClick={() => inputRef.current?.click()}>{__("เปลี่ยนไฟล์")}</Button>
            )}
          </Stack>
        ) : (
          <Stack alignItems="center" spacing={1.5}>
            <CloudUpload color="primary" sx={{ fontSize: 54 }} />
            <Typography fontWeight={700}>{__("ลากไฟล์ PDF มาวางที่นี่")}</Typography>
            <Typography variant="body2" color="text.secondary">{__("หรือ")}</Typography>
            <Button variant="outlined" onClick={() => inputRef.current?.click()}>
              {__("เลือกไฟล์ PDF")}
            </Button>
          </Stack>
        )}
        <GlobalFileInput
          ref={inputRef}
          aria-label={__("เลือกไฟล์ PDF")}
          accept="application/pdf,.pdf"
          disabled={uploading}
          onChange={(event) => {
            chooseFile(event.target.files?.[0]);
            event.target.value = "";
          }}
        />
      </Box>
      {uploading && (
        <Box mt={2.5}>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 8 }} />
          <Typography variant="body2" color="text.secondary" mt={1} textAlign="center">
            {__("กำลัง Upload {{progress}}%", { progress: progress ?? 0 })}
          </Typography>
        </Box>
      )}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 3 }}>
      <Button disabled={uploading} onClick={onClose}>{__("ยกเลิก")}</Button>
      <Button
        variant="contained"
        startIcon={<CloudUpload />}
        disabled={!file || uploading}
        onClick={() => void submit()}
      >
        {__("เริ่ม Upload")}
      </Button>
    </DialogActions>
  </Dialog>;
}
